<template>
  <v-card class="minContent">
    <v-card-title>
      <v-menu
        offset-y
        v-if="hasUserRight('manageBaseData')"
      >
        <template v-slot:activator="{ on }">
          <v-btn
            v-on="on"
            :disabled="selected.length === 0"
            color="secondary"
          >
            <v-icon class="mr-2">mdi-menu-down</v-icon>
            {{ $t("base.actions") }}
          </v-btn>
        </template>

        <v-list>
          <v-list-item
            @click="editDealer()"
            :disabled="selected.length !== 1"
          >
            <v-icon class="mr-4">mdi-pencil</v-icon>
            {{ capitalizeString("base.edit")}}
          </v-list-item>
          <v-list-item @click="deleteDealers()">
            <v-icon class="mr-4">mdi-delete-forever</v-icon>
            {{ capitalizeString("base.delete")}}
          </v-list-item>
          <v-list-item @click="exportData()">
            <v-icon class="mr-4">mdi-export</v-icon>
            {{ capitalizeString('base.export') }}
          </v-list-item>
        </v-list>
      </v-menu>
      <span
        class="ml-8 body-1"
        v-if="hasUserRight('manageBaseData')"
      >
        {{ selected.length + " " + $t('base.of') + " " + filteredList.length + " " + $t('base.selected') }}
      </span>
      <v-spacer />
      <v-btn
        v-if="hasUserRight('manageBaseData')"
        @click="createDealer()"
        color="secondary"
        class="mr-8"
        v-tooltip="$tooltip($t('condAdmin.tooltip.newDealer'))"
      >
        <v-icon class="mr-2">mdi-plus</v-icon>
        {{ $t("base.new") }}
      </v-btn>
      <v-icon
        @click="getDealerList()"
        color="secondary"
        class="mr-8"
        v-tooltip="$tooltip($t('condAdmin.tooltip.refresh'))"
      >mdi-reload</v-icon>
      <v-menu
        offset-y
        :close-on-content-click="false"
      >
        <template v-slot:activator="{ on }">
          <v-icon
            v-on="on"
            color="secondary"
            v-tooltip="$tooltip($t('condAdmin.tooltip.menu'))"
          >mdi-format-columns</v-icon>
        </template>
        <v-list>
          <v-list-item
            v-for="(col, index) in availableColumns"
            :key="index"
          >
            <v-checkbox
              v-model="availableColumns[index].active"
              :label="$t(availableColumns[index].text)"
              @change="setAvailableHeaders(availableColumns[index],$event)"
            ></v-checkbox>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-icon
        @click="saveUserTableSettings('dealerTable')"
        color="secondary"
        class="ml-8"
        v-tooltip="$tooltip($t('condAdmin.tooltip.save'))"
      >mdi-content-save-cog</v-icon>
      <JsonImporter
        v-if="hasUserRight('manageBaseData')"
        class="ml-8"
        objectType="dealers"
        module="condAdmin"
        :preImportCheck="preImportCheckFunction"
        @import="importData"
      />
    </v-card-title>

    <v-card-text class="pt-4">
      <v-row no-gutters>
        <v-col cols="12">
          <v-data-table
            v-model="selected"
            :headers="listHeaders"
            :items="filteredList"
            :items-per-page="footerProps.itemsPerPage"
            :footer-props="footerProps"
            item-key="id"
            show-select
            fixed-header
            :sort-by="listSortKey"
            @click:row="clickedRow"
            :loading="$store.state.condAdmin.dealersLoading"
            @update:items-per-page="getItemPerPage"
          >
            <template v-slot:body.prepend="{}">
              <tr>
                <td></td>
                <td
                  v-for="(h, index) in listHeaders"
                  :key="index"
                >
                  <SearchField
                    v-if="filter.hasOwnProperty(h.value)"
                    :value="filter[h.value].value"
                    @filter="applyFilters($event, h.value)"
                  />
                </td>
              </tr>
            </template>
            <template v-slot:header.data-table-select="{ on, props }">
              <v-simple-checkbox
                color="secondary"
                v-bind="props"
                v-on="on"
              ></v-simple-checkbox>
            </template>
            <template v-slot:header.menu="{}">
            </template>
            <template v-slot:item.state="{ item }">
              <v-chip :class="getStateClass(item)">
                {{ getStateText(item) }}
              </v-chip>
            </template>
            <template v-slot:item.menu="{ item }">
              <v-menu
                offset-y
                v-if="hasUserRight('manageBaseData')"
              >
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    icon
                  >
                    <v-icon>mdi-dots-vertical</v-icon>
                  </v-btn>
                </template>

                <v-list>
                  <v-list-item @click="editDealer(item)">
                    <v-icon class="mr-4">mdi-pencil</v-icon>
                    {{ $t("base.edit")}}
                  </v-list-item>
                  <v-list-item @click="deleteDealer(item)">
                    <v-icon class="mr-4">mdi-delete-forever</v-icon>
                    {{ $t("base.delete")}}
                  </v-list-item>
                </v-list>
              </v-menu>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>
    <JsonExporter
      objectType="dealers"
      module="condAdmin"
      ref="jsonExporter"
    />
    <DoubletDlg
      ref="doubletDlg"
      :uniqueCheckFunction="importUniqueCheck"
      :fields="doubletFields"
      :hint="this.$t('condAdmin.dealerList.importDoublet')"
    />
  </v-card>
</template>

<script lang="js">
import { refreshDealerList, saveDealer } from '../js/condAdminDealerHelper.js';
import { isProcessStateOK, newMessage } from '@/base/js/ProcessDataHelper';
import condAdminMixin from '../js/condAdminMixin.js';
import { complexFilter } from '@/base/js/ObjectHelper';
import SearchField from '@/common/SearchField';
import JsonImporter from '@/common/JsonImporter';
import JsonExporter from '@/common/JsonExporter';
import DoubletDlg from '@/common/DoubletDlg';

export default {
  name: 'DealerTable',
  components: {
    SearchField,
    JsonImporter,
    JsonExporter,
    DoubletDlg
  },
  mixins: [ condAdminMixin ],
  data () {
    return {
      selected: [],
      listSortKey: 'name',
      footerProps: {
        showFirstLastPage: true,
        showCurrentPage: true,
        itemsPerPageOptions: [5, 15, 30, 50, -1],
        itemsPerPage: 15
      },
      filteredList: [],
      availableColumns: [
        { text: this.$t('condAdmin.dealer.dealerKey'), active: true, value: 'dealerKey' },
        { text: this.$t('base.contact.name'), active: true, value: 'name' },
        { text: this.$t('base.contact.street'), active: true, value: 'street' },
        { text: this.$t('base.contact.zipCode'), active: true, value: 'zipCode' },
        { text: this.$t('base.contact.city'), active: true, value: 'city' },
        { text: this.$t('base.contact.country'), active: false, value: 'country' },
        { text: this.$t('base.contact.phone'), active: false, value: 'phone' },
        { text: this.$t('base.contact.email'), active: false, value: 'email' },
        { text: this.$t('base.contact.fax'), active: false, value: 'fax' },
        { text: this.$t('base.contact.additionalData'), active: false, value: 'additionalData' },
        { text: this.$t('condAdmin.dealer.conditionMatch'), active: false, value: 'conditionMatch', sortable: false },
        { text: this.$t('base.state'), active: true, value: 'state' }
      ],
      filter: {
        dealerKey: { condition: 'startsWith', value: null, type: 'SearchField' },
        name: { condition: 'startsWith', value: null, type: 'SearchField' },
        street: { condition: 'startsWith', value: null, type: 'SearchField' },
        zipCode: { condition: 'startsWith', value: null, type: 'SearchField' },
        city: { condition: 'startsWith', value: null, type: 'SearchField' },
        country: { condition: 'startsWith', value: null, type: 'SearchField' }
      },
      doubletFields: [
        { key: 'dealerKey', text: this.$t('condAdmin.dealer.dealerKey'), unique: true, readonly: false },
        { key: 'name', text: this.$t('base.contact.name'), unique: false, readonly: false },
        { key: 'zipCode', text: this.$t('base.contact.zipCode'), unique: false, readonly: false },
        { key: 'city', text: this.$t('base.contact.city'), unique: false, readonly: false }
      ]
    };
  },
  computed: {
    listHeaders () {
      let h = [];
      for (let mc of this.availableColumns) {
        if (mc.active === true) {
          h.push(mc);
        }
      }
      h.push({ text: 'menu', value: 'menu', sortable: false });
      return h;
    }
  },
  mounted () {
    this.getUserTableSettings('dealerTable');
    this.filteredList = complexFilter(this.$store.state.condAdmin.dealerList, this.filter);
    this.$emit('setPageMode', 'table');
  },
  methods: {
    async getDealerList () {
      await refreshDealerList();
      this.filteredList = complexFilter(this.$store.state.condAdmin.dealerList, this.filter);
    },
    async deleteDealers () {
      if (await this.$globals.Confirm.yesNoDlg(this.$t('condAdmin.dealerList.deleteDealer'), this.$t('condAdmin.dealerList.deleteDealersConfirmText'))) {
        for (let dealer of this.selected) {
          this.callDeleteDealerService(dealer);
        }
      }
    },
    async deleteDealer (dealer) {
      // show confirmation dialog
      if (await this.$globals.Confirm.yesNoDlg(this.$t('condAdmin.dealerList.deleteDealer') + ': ' + dealer.name, this.$t('condAdmin.dealerList.deleteDealerConfirmText'))) {
        this.callDeleteDealerService(dealer);
      }
    },
    callDeleteDealerService (dealer) {
      let ioData = {
        clientKey: this.$store.state.condAdmin.clientKey,
        dealer: {
          id: dealer.id
        }
      };
      this.$restClient.callProcess('condAdmin', 'deleteDealer', ioData).then(
        (processData) => {
          if (isProcessStateOK(processData)) {
            this.$globals.Info.open([{ type: 'success', message: this.$t('condAdmin.dealerList.deletedDealer', { dealer: dealer.name }) }]);
          } else {
            this.$globals.Info.open(processData.processState.messages);
          }
          this.$store.state.condAdmin.dealerList.splice(
            this.$store.state.condAdmin.dealerList.findIndex((p) => p.id === dealer.id), 1
          );
        }
      );
    },
    createDealer () {
      this.$router.push('/condAdmin/dealer/create');
    },
    editDealer (dealer = null) {
      if (!dealer) {
        dealer = this.selected[0];
      }
      this.$router.push('/condAdmin/dealer/' + dealer.id);
    },
    clickedRow (dealer) {
      this.editDealer(dealer);
    },
    getItemPerPage (val) {
      this.footerProps.itemsPerPage = val;
      this.$store.state.condAdmin.userSettings['dealerTable'].footerPropItemsPerPage = val;
    },
    setAvailableHeaders (column, value) {
      this.$store.state.condAdmin.userSettings['dealerTable'].columns[column.value] = value;
    },
    applyFilters (value, filtername) {
      this.filter[filtername].value = value;
      this.filteredList = complexFilter(this.$store.state.condAdmin.dealerList, this.filter);
      this.$store.state.condAdmin.userSettings['dealerTable'].filter[filtername] = value;
    },
    exportData () {
      let dealers = JSON.parse(JSON.stringify(this.selected));
      for (let d of dealers) {
        delete d.clientKey;
        delete d.id;
        delete d.state;
        delete d.timestamp;
      }
      this.$refs.jsonExporter.exportJsonData({ dealers });
    },
    async importData (json) {
      await refreshDealerList();
      let imported = 0;
      let total = json.data.dealers.length;

      for (let imp of json.data.dealers) {
        // check already existing dealerKey
        let existing = this.importUniqueCheck(imp);
        if (existing != null) {
          let [action, merged] = await this.$refs.doubletDlg.show(existing, imp);
          switch (action) {
            case 'overwrite':
              merged.id = existing.id;
              await saveDealer(merged);
              // console.log('imported', JSON.stringify(merged));
              imported++;
              break;
            case 'copy':
              await saveDealer(merged);
              // console.log('imported', JSON.stringify(merged));
              imported++;
              break;
            case 'skipNew':
            default:
              break;
          }
        } else {
          await saveDealer(imp);
          // console.log('imported', JSON.stringify(imp));
          imported++;
        }
      }
      this.$globals.Info.open([ newMessage('success', this.$t('condAdmin.importResult', { imported, total })) ]);
      if (imported > 0) this.getDealerList();
    },
    preImportCheckFunction (json) {
      if (json.data.dealers == null || json.data.dealers.length < 1) {
        this.$globals.Info.open([ newMessage('userError', this.$t('condAdmin.dealerList.noImportData')) ]);
        return false;
      }
      return true;
    },
    importUniqueCheck (merged) {
      let existing = this.$store.state.condAdmin.dealerList.find((i) => i.dealerKey === merged.dealerKey);
      return existing;
    }
  }
};
</script>

<style scoped>
</style>
